import React from 'react';
import { metadataMeta } from '~/lib/utils/meta-utils-client';
import { metadataLoader } from '~/lib/utils/meta-utils-server';

export const loader = metadataLoader((t) => ({
  title: t('common.routes.termsConditions.title'),
  description: t('common.routes.termsConditions.description'),
}));

export const meta = metadataMeta<typeof loader>();

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-6">
    <h2 className="text-2xl font-bold">{title}</h2>
    <div className="space-y-2 opacity-70">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <main className="max-w-prose space-y-12 p-8 pt-16">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">ScoreOrNot Terms and Conditions</h1>
        <p className="text-sm opacity-50">Last Updated: August 1, 2025</p>
      </header>

      <p>
        Welcome to ScoreOrNot! These Terms and Conditions (&quot;Terms&quot;)
        govern your use of our sports prediction platform and services. By
        accessing or using ScoreOrNot, you agree to be bound by these Terms.
      </p>

      <Section title="1. About ScoreOrNot">
        <p>
          ScoreOrNot is a sports prediction platform where users predict
          outcomes of sports video clips in a competitive, gamified environment.
          Our platform is skill-based and does not involve betting or gambling.
        </p>
      </Section>

      <Section title="2. Eligibility and Account Registration">
        <ul>
          <li>ScoreOrNot is available to users worldwide.</li>
          <li>Users of all ages may use the platform.</li>
          <li>Users under 13 should have parental supervision.</li>
          <li>
            You must provide accurate information when creating an account.
          </li>
          <li>
            You are responsible for maintaining the security of your account.
          </li>
        </ul>
      </Section>

      <Section title="3. Platform Usage">
        <h3>Acceptable Use</h3>
        <ul>
          <li>Use ScoreOrNot for legitimate sports prediction activities.</li>
          <li>
            Respect other users and maintain a positive community environment.
          </li>
          <li>Do not attempt to manipulate or exploit the platform.</li>
        </ul>
        <h3>Prohibited Activities</h3>
        <ul>
          <li>
            Creating multiple accounts from the same device or IP address.
          </li>
          <li>Using automated systems, bots, or scripts.</li>
          <li>Attempting to hack, disrupt, or damage the platform.</li>
          <li>Sharing inappropriate or offensive content.</li>
          <li>Violating any applicable laws or regulations.</li>
        </ul>
      </Section>

      <Section title="4. Technical Considerations">
        <ul>
          <li>
            ScoreOrNot is provided &quot;as-is&quot; and may experience
            occasional technical issues.
          </li>
          <li>
            We are not liable for service interruptions, connectivity issues, or
            device-specific problems.
          </li>
          <li>
            Platform performance may vary based on internet connection, device
            capabilities, and network conditions.
          </li>
          <li>
            We continuously work to improve platform stability but cannot
            guarantee uninterrupted service.
          </li>
        </ul>
      </Section>

      <Section title="5. Content and Intellectual Property">
        <ul>
          <li>
            All content on ScoreOrNot, including videos, graphics, and software,
            is owned by ScoreOrNot or our content partners.
          </li>
          <li>Users retain ownership of any personal data they provide.</li>
          <li>
            We have agreements with content creators regarding the use of sports
            clips.
          </li>
          <li>
            Users may not reproduce, distribute, or create derivative works from
            platform content.
          </li>
        </ul>
      </Section>

      <Section title="6. Privacy and Data">
        <ul>
          <li>
            We collect and use your information in accordance with our Privacy
            Policy.
          </li>
          <li>
            We value user privacy and implement appropriate security measures.
          </li>
          <li>
            User data may be retained to improve our services and user
            experience.
          </li>
          <li>We do not sell personal information to third parties.</li>
        </ul>
      </Section>

      <Section title="7. Monitoring and Account Management">
        <ul>
          <li>
            We monitor platform activity to ensure fair play and detect
            suspicious behavior.
          </li>
          <li>
            We reserve the right to suspend or terminate accounts that violate
            these Terms.
          </li>
          <li>
            Account termination may occur for multiple account creation, abuse,
            or other violations.
          </li>
          <li>
            Users will be notified of account actions when reasonably possible.
          </li>
        </ul>
      </Section>

      <Section title="8. Monetization and Payments">
        <ul>
          <li>ScoreOrNot currently operates as a freemium platform.</li>
          <li>Future monetization features may be introduced.</li>
          <li>
            Any payment terms will be clearly communicated before
            implementation.
          </li>
          <li>
            Content creators have separate agreements regarding monetization.
          </li>
        </ul>
      </Section>

      <Section title="9. Disclaimers and Limitations">
        <ul>
          <li>ScoreOrNot is provided for entertainment purposes.</li>
          <li>
            We make no guarantees about platform availability or performance.
          </li>
          <li>Users participate at their own risk.</li>
          <li>
            We are not liable for any indirect, incidental, or consequential
            damages.
          </li>
          <li>
            Our total liability is limited to the amount paid by the user, if
            any.
          </li>
        </ul>
      </Section>

      <Section title="10. Dispute Resolution">
        <ul>
          <li>
            Disputes should first be addressed through our customer support.
          </li>
          <li>We encourage good faith resolution of any issues.</li>
          <li>For legal disputes, the laws of Slovenia shall apply.</li>
          <li>Users agree to attempt mediation before pursuing litigation.</li>
        </ul>
      </Section>

      <Section title="11. Changes to Terms">
        <ul>
          <li>We may update these Terms periodically.</li>
          <li>Material changes will be communicated to users.</li>
          <li>
            Continued use of ScoreOrNot constitutes acceptance of updated Terms.
          </li>
        </ul>
      </Section>

      <Section title="12. Termination">
        <ul>
          <li>Either party may terminate this agreement at any time.</li>
          <li>
            Upon termination, your right to use ScoreOrNot ceases immediately.
          </li>
          <li>Certain provisions of these Terms survive termination.</li>
        </ul>
      </Section>

      <Section title="13. General Provisions">
        <ul>
          <li>
            These Terms constitute the entire agreement between you and
            ScoreOrNot.
          </li>
          <li>
            If any provision is found unenforceable, the remainder remains in
            effect.
          </li>
          <li>
            Our failure to enforce any right does not constitute a waiver.
          </li>
        </ul>
      </Section>

      <Section title="14. Contact Information">
        <p>For questions about these Terms, please contact us at:</p>
        <p>
          <strong>Email:</strong>{' '}
          <a href="mailto:contact@scoreornot.com">contact@scoreornot.com</a>
        </p>
        <p>
          <strong>Website:</strong>{' '}
          <a
            href="https://scoreornot.com"
            target="_blank"
            rel="noopener noreferrer">
            scoreornot.com
          </a>
        </p>
      </Section>

      <footer>
        <p>
          By using ScoreOrNot, you acknowledge that you have read, understood,
          and agree to be bound by these Terms and Conditions.
        </p>
      </footer>
    </main>
  );
}
